/**
 * AI SOAPテンプレート生成クエリ/ミューテーション（TanStack Query）
 *
 * ## 設計上の注意点（将来の開発者向け）
 *
 * このフックは以下の理由から、標準的なTanStack Queryパターンとは異なる設計になっています:
 *
 * 1. **複雑な状態遷移**: AI生成には「重複検知→マージ/リネーム選択」という
 *    複数ステップのワークフローがあり、単純なmutationでは表現できません。
 *    そのため、フェーズ管理用のローカル状態を併用しています。
 *
 * 2. **キャンセル機能**: AbortControllerによるリクエストキャンセルが必要で、
 *    TanStack Queryのキャンセル機能だけでは不十分なケースがあります。
 *
 * 3. **重複検知エラーの特殊処理**: APIが重複を検知した場合、エラーではなく
 *    「次のアクション選択」状態に遷移する必要があります。
 *
 * ## 将来の改善案
 *
 * - 状態遷移が複雑になる場合は、XStateなどのステートマシンライブラリの導入を検討
 * - 重複検知フローをAPIレベルで簡素化できれば、よりシンプルなmutationに統一可能
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef, useCallback } from 'react'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  AiGenerationRequest,
  AiGenerationUsageInfo,
  DuplicateDetectionResponse,
  DuplicateDetectionError,
} from '@/features/internal/drugs/types/soapTemplate'
import {
  aiGenerateSoapTemplates,
  getAiGenerationUsage,
} from '@/features/internal/drugs/apis/soapTemplate'

// ========================================
// 型定義
// ========================================

type AiGenerationPhase =
  | 'idle'
  | 'generating'
  | 'duplicate_detected'
  | 'processing_merge'
  | 'processing_rename'

interface AiGenerationState {
  phase: AiGenerationPhase
  requests: AiGenerationRequest[]
  duplicateData?: DuplicateDetectionResponse
  error?: string
}

// ========================================
// ヘルパー関数
// ========================================

const isDuplicateDetectionError = (
  error: unknown
): error is DuplicateDetectionError => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'type' in error &&
    (error as DuplicateDetectionError).type === 'DUPLICATE_DETECTION' &&
    'data' in error
  )
}

const isAbortError = (error: unknown): boolean => {
  if (error === null || typeof error !== 'object') {
    return false
  }
  const errorObj = error as { message?: string; name?: string }
  return (
    errorObj.message === 'Request was aborted' || errorObj.name === 'AbortError'
  )
}

const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object') {
    const errorObj = error as {
      response?: { data?: { error?: string } }
      message?: string
    }
    return (
      errorObj.response?.data?.error ||
      errorObj.message ||
      'AI生成中にエラーが発生しました。しばらくしてから再度お試しください。'
    )
  }
  return 'AI生成中にエラーが発生しました。しばらくしてから再度お試しください。'
}

// ========================================
// useAiGenerationUsageQuery
// ========================================

/**
 * AI生成利用状況クエリ
 *
 * 責務:
 * - AI生成の利用状況（残り回数など）を取得
 * - 補助情報のためエラー時もサイレントに処理
 */
export const useAiGenerationUsageQuery = (enabled = false) => {
  return useQuery({
    queryKey: queryKeys.aiGeneration.usage(),
    queryFn: async (): Promise<AiGenerationUsageInfo | null> => {
      try {
        const response = await getAiGenerationUsage()
        return response.data
      } catch {
        // 利用状況は補助情報のため、エラー時はnullを返す
        return null
      }
    },
    enabled,
    staleTime: 1000 * 60, // 1分キャッシュ
  })
}

// ========================================
// useAiSoapTemplateGeneration（統合フック）
// ========================================

/**
 * AI SOAPテンプレート生成フック（TanStack Query + ローカル状態）
 *
 * 注意: このフックは複雑な状態遷移を管理するため、
 * TanStack Queryのmutationとローカル状態を併用しています。
 * 詳細はファイル先頭のコメントを参照してください。
 */
export const useAiSoapTemplateGeneration = () => {
  const queryClient = useQueryClient()

  // フェーズ管理用ローカル状態
  // （TanStack Queryでは表現しにくい複雑な状態遷移のため）
  const [state, setState] = useState<AiGenerationState>({
    phase: 'idle',
    requests: [],
  })

  // キャンセル用AbortController
  const abortControllerRef = useRef<AbortController | null>(null)

  // 利用状況クエリ
  const usageQuery = useAiGenerationUsageQuery(false)

  // 状態更新ヘルパー
  const updateState = useCallback((updates: Partial<AiGenerationState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  // 状態リセット
  const resetState = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setState({ phase: 'idle', requests: [] })
  }, [])

  // AI生成Mutation
  // 分割代入で必要なプロパティを取り出す（@tanstack/query/no-unstable-deps 対応）
  const {
    mutateAsync: generateMutateAsync,
    reset: generateReset,
    isPending: generateIsPending,
  } = useMutation({
    mutationFn: async ({
      requests,
      forceMerge,
      signal,
    }: {
      requests: AiGenerationRequest[]
      forceMerge?: boolean
      signal?: AbortSignal
    }) => {
      const response = await aiGenerateSoapTemplates(
        { requests, force_merge: forceMerge },
        signal
      )
      return response
    },
    onSuccess: (response) => {
      // 利用状況を更新
      if (response.data?.usage_info) {
        queryClient.setQueryData(
          queryKeys.aiGeneration.usage(),
          response.data.usage_info
        )
      }
      // 成功時は状態をリセット
      updateState({ phase: 'idle' })
    },
    // onErrorは使用しない（複雑なエラーハンドリングはexecuteGeneration内で行う）
  })

  // 利用状況取得
  const fetchUsageInfo = useCallback(async () => {
    await usageQuery.refetch()
  }, [usageQuery])

  // AbortController作成
  const createAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller
    return controller
  }, [])

  // AI生成実行
  const executeGeneration = useCallback(
    async (
      requests: AiGenerationRequest[],
      options?: { forceMerge?: boolean }
    ) => {
      // 実行中チェック
      if (generateIsPending) {
        throw new Error(
          '別のAI生成処理が実行中です。完了してから再度お試しください。'
        )
      }

      const abortController = createAbortController()

      updateState({
        phase: options?.forceMerge ? 'processing_merge' : 'generating',
        requests,
        error: undefined,
      })

      try {
        const response = await generateMutateAsync({
          requests,
          forceMerge: options?.forceMerge,
          signal: abortController.signal,
        })

        if (abortController.signal.aborted) {
          return null
        }

        return response
      } catch (error: unknown) {
        if (isAbortError(error)) {
          return null
        }

        // 重複検知エラー: 特殊な状態遷移
        if (isDuplicateDetectionError(error)) {
          updateState({
            phase: 'duplicate_detected',
            duplicateData: error.data,
          })
          throw error
        }

        // その他のエラー
        updateState({
          phase: 'idle',
          error: extractErrorMessage(error),
        })
        throw error
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }
    },
    [generateIsPending, generateMutateAsync, createAbortController, updateState]
  )

  // マージ実行
  const executeMerge = useCallback(async () => {
    return executeGeneration(state.requests, { forceMerge: true })
  }, [executeGeneration, state.requests])

  // リネーム実行
  const executeRename = useCallback(
    async (newRequests: AiGenerationRequest[]) => {
      updateState({
        phase: 'processing_rename',
        requests: newRequests,
      })

      try {
        const response = await executeGeneration(newRequests)
        updateState({ duplicateData: undefined })
        return response
      } catch (error: unknown) {
        if (isDuplicateDetectionError(error)) {
          updateState({
            phase: 'duplicate_detected',
            requests: newRequests,
            duplicateData: error.data,
          })
        }
        throw error
      }
    },
    [executeGeneration, updateState]
  )

  // キャンセル
  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    generateReset()
    resetState()
  }, [generateReset, resetState])

  // 計算されたプロパティ
  const isLoading = generateIsPending
  const isGenerating = state.phase === 'generating'
  const isDuplicateDetected = state.phase === 'duplicate_detected'
  const isProcessing =
    state.phase === 'processing_merge' || state.phase === 'processing_rename'
  const canGenerate = !generateIsPending && state.phase === 'idle'

  return {
    // 状態（usageInfoはUI表示・生成可否判定に使用）
    state: {
      ...state,
      usageInfo: usageQuery.data ?? undefined,
    },
    isLoading,
    isGenerating,
    isDuplicateDetected,
    isProcessing,
    isGenerationLocked: generateIsPending,
    canGenerate,

    // アクション
    executeGeneration,
    executeMerge,
    executeRename,
    cancelGeneration,
    resetState,
    fetchUsageInfo,
    updateState,
  }
}
